import React from "react";
import { Edges } from "@react-three/drei";
import { DoubleSide } from "three";
import {
  MM,
  BORDO,
  SPESSORE_MURO,
  PORTINA_LAT_LARGHEZZA,
  PORTINA_LAT_ALTEZZA,
  PORTINA_LAT_GAP,
  C_MURO,
  C_GARAGE_FONDO,
  C_BORDO_SCURO,
} from "./constants";

const VANO_DIST_FRONTE_M = MM(500);

export const GarageWalls: React.FC<{
  larghezza: number;
  altezza: number;
  profondita: number;
  showPortinaLaterale: boolean;
  vanoConAltroAccesso: boolean;
  /**
   * Distanza reale (metri) fra il foro e ciascuna parete laterale/il soffitto:
   * la spalletta e' letteralmente quella parete, l'architrave quel margine
   * sopra il foro. A 0 la parete si sposta fino al bordo del foro, quindi la
   * porta vi risulta appoggiata contro. Default BORDO per compatibilita'.
   */
  margineSx?: number;
  margineDx?: number;
  margineTop?: number;
}> = ({ larghezza, altezza, profondita, showPortinaLaterale, vanoConAltroAccesso, margineSx, margineDx, margineTop }) => {
  const SM     = SPESSORE_MURO;
  const marSx  = margineSx ?? BORDO;
  const marDx  = margineDx ?? BORDO;
  const marTop = margineTop ?? BORDO;
  const P      = profondita;
  const altExt = altezza + marTop;

  const extraDx = showPortinaLaterale ? PORTINA_LAT_GAP + PORTINA_LAT_LARGHEZZA : 0;
  const totalW  = larghezza + extraDx + marSx + marDx + 2 * SM;
  const centerX = (larghezza + extraDx + marDx - marSx) / 2;

  // Il muro frontale occupa z da -SM/2+0.01 a +SM/2+0.01. Le pareti laterali
  // partono a filo della sua faccia esterna; il vano interno (profondo P)
  // parte dalla faccia interna.
  const zEsterno = SM / 2 + 0.01;
  const zInterno = -SM / 2 + 0.01;
  const PLat     = P + SM;
  const zLat     = zEsterno - PLat / 2;
  const zFondo   = zInterno - P - SM / 2;
  const wallX  = -marSx - SM / 2;

  const zWallFront = zLat + PLat / 2;
  const zVNear     = zWallFront - VANO_DIST_FRONTE_M;
  const zVFar      = zVNear - PORTINA_LAT_LARGHEZZA;
  const dFront     = VANO_DIST_FRONTE_M;
  const dBack      = PLat - VANO_DIST_FRONTE_M - PORTINA_LAT_LARGHEZZA;
  const hTop       = altExt - PORTINA_LAT_ALTEZZA;

  return (
    <>
      {/* Parete sinistra */}
      {!vanoConAltroAccesso ? (
        <mesh position={[wallX, altExt / 2, zLat]}>
          <boxGeometry args={[SM, altExt, PLat]} />
          <meshStandardMaterial color={C_MURO} side={DoubleSide} />
          <Edges color={C_BORDO_SCURO} />
        </mesh>
      ) : (
        <>
          <mesh position={[wallX, altExt / 2, zWallFront - dFront / 2]}>
            <boxGeometry args={[SM, altExt, dFront]} />
            <meshStandardMaterial color={C_MURO} side={DoubleSide} />
            <Edges color={C_BORDO_SCURO} />
          </mesh>
          {dBack > 0 && (
            <mesh position={[wallX, altExt / 2, zVFar - dBack / 2]}>
              <boxGeometry args={[SM, altExt, dBack]} />
              <meshStandardMaterial color={C_MURO} side={DoubleSide} />
              <Edges color={C_BORDO_SCURO} />
            </mesh>
          )}
          {hTop > 0 && (
            <mesh position={[wallX, PORTINA_LAT_ALTEZZA + hTop / 2, (zVNear + zVFar) / 2]}>
              <boxGeometry args={[SM, hTop, PORTINA_LAT_LARGHEZZA]} />
              <meshStandardMaterial color={C_MURO} side={DoubleSide} />
              <Edges color={C_BORDO_SCURO} />
            </mesh>
          )}
        </>
      )}

      {/* Parete destra */}
      <mesh position={[larghezza + extraDx + marDx + SM / 2, altExt / 2, zLat]}>
        <boxGeometry args={[SM, altExt, PLat]} />
        <meshStandardMaterial color={C_MURO} side={DoubleSide} />
        <Edges color={C_BORDO_SCURO} />
      </mesh>

      {/* Parete di fondo */}
      <mesh position={[centerX, altExt / 2, zFondo]}>
        <boxGeometry args={[totalW, altExt, SM]} />
        <meshStandardMaterial color={C_GARAGE_FONDO} side={DoubleSide} />
        <Edges color={C_BORDO_SCURO} />
      </mesh>
    </>
  );
};
